import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info } from "lucide-react";

interface DeviceBrowserSettingsContentProps {
  deviceId: string;
  browserMode: "filtered" | "whitelist" | "none";
  onBrowserModeChange: (mode: "filtered" | "whitelist" | "none") => void;
  nsfwLevel: "light" | "medium" | "strict";
  onNsfwLevelChange: (level: "light" | "medium" | "strict") => void;
  skinToneColoring: boolean;
  onSkinToneChange: (value: boolean) => void;
  blockVideos: boolean;
  onBlockVideosChange: (value: boolean) => void;
  accuracy: "fastest" | "balanced" | "accurate";
  onAccuracyChange: (value: "fastest" | "balanced" | "accurate") => void;
}

export const DeviceBrowserSettingsContent = ({
  deviceId,
  browserMode,
  onBrowserModeChange,
  nsfwLevel,
  onNsfwLevelChange,
  skinToneColoring,
  onSkinToneChange,
  blockVideos,
  onBlockVideosChange,
  accuracy,
  onAccuracyChange,
}: DeviceBrowserSettingsContentProps) => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Browser Settings</h1>
        <p className="text-sm text-muted-foreground">Configure browser access and image filtering for this device</p>
      </div>

      {/* Browser Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Browser Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={browserMode}
            onValueChange={(val: "filtered" | "whitelist" | "none") => onBrowserModeChange(val)}
          >
            <div className="flex items-start gap-3 p-3 rounded-lg border">
              <RadioGroupItem value="filtered" id="browser-filtered" />
              <div>
                <Label htmlFor="browser-filtered" className="font-medium">Filtered Browser</Label>
                <p className="text-sm text-muted-foreground">Allow browser with category filters and optional blacklists.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border">
              <RadioGroupItem value="whitelist" id="browser-whitelist" />
              <div>
                <Label htmlFor="browser-whitelist" className="font-medium">Whitelisted Browser</Label>
                <p className="text-sm text-muted-foreground">Allow only explicitly whitelisted sites. Categories and blacklist are hidden.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg border">
              <RadioGroupItem value="none" id="browser-none" />
              <div>
                <Label htmlFor="browser-none" className="font-medium">No Browser</Label>
                <p className="text-sm text-muted-foreground">Completely block all browser access. Only Settings remain visible.</p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Image Filtering */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Image Filtering</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Skin Tone Coloring */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Skin Tone Coloring</div>
              <p className="text-sm text-muted-foreground">Apply skin coloring adjustment alongside NSFW blocking (optional).</p>
            </div>
            <Switch checked={skinToneColoring} onCheckedChange={onSkinToneChange} />
          </div>

          {/* Offensive Image Blocking Level (mandatory) */}
          <div className="space-y-2">
            <div className="font-medium">Offensive Image Blocking</div>
            <p className="text-sm text-muted-foreground">Choose the minimum required blocking level (one option is mandatory).</p>
            <RadioGroup
              value={nsfwLevel}
              onValueChange={(val: "light" | "medium" | "strict") => onNsfwLevelChange(val)}
            >
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <RadioGroupItem value="light" id="nsfw-light" />
                <div>
                  <Label htmlFor="nsfw-light" className="font-medium">Light</Label>
                  <p className="text-sm text-muted-foreground">Block adult images only.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <RadioGroupItem value="medium" id="nsfw-medium" />
                <div>
                  <Label htmlFor="nsfw-medium" className="font-medium">Medium</Label>
                  <p className="text-sm text-muted-foreground">Block partial nudity and up.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <RadioGroupItem value="strict" id="nsfw-strict" />
                <div>
                  <Label htmlFor="nsfw-strict" className="font-medium">Strict</Label>
                  <p className="text-sm text-muted-foreground">Block even a lot of exposed skin.</p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="w-4 h-4 mt-0.5" />
            <p>Skin Tone Coloring can be combined with any NSFW level, or you can use NSFW-only. An NSFW option is always required.</p>
          </div>
        </CardContent>
      </Card>

      {/* Video Filtering */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Video Filtering</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Videos</div>
              <p className="text-sm text-muted-foreground">Control access to video content on websites.</p>
            </div>
            <Select value={blockVideos ? "block" : "allow"} onValueChange={(value) => onBlockVideosChange(value === "block")}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">Allow</SelectItem>
                <SelectItem value="block">Block</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Accuracy Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Accuracy Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Accuracy</div>
              <p className="text-sm text-muted-foreground">Choose the accuracy level for content filtering.</p>
            </div>
            <Select value={accuracy} onValueChange={onAccuracyChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="fastest">Fastest</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="accurate">Accurate</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};